// 교회앱관리 / 교적관리 / 재정관리는 같은 담당자 계정(churchAdmin.admins)을 쓰지만 로그인은 콘솔마다 따로 한다.
// 그래서 세션도 콘솔별 키에 따로 저장한다 — 한 콘솔에 로그인해도 다른 콘솔은 다시 로그인해야 한다.
export type ConsoleKey = 'app' | 'roll' | 'finance';

export const CONSOLE_INFO: Record<ConsoleKey, { label: string; path: string }> = {
  app: { label: '교회앱관리', path: '/manage-app' },
  roll: { label: '교적관리', path: '/manage-roll' },
  finance: { label: '재정관리', path: '/manage-finance' },
};

export function isConsoleKey(value: unknown): value is ConsoleKey {
  return value === 'app' || value === 'roll' || value === 'finance';
}

function storageKey(consoleKey: ConsoleKey) {
  return `userSession:${consoleKey}`;
}

export type UserSession = {
  id: number;
  userAccount: string;
  userNickName: string;
  userSort: string;
  userDetail: string;
  grade: string;
  authInstitution: string;
  authChurch: string;
  authDepartment: string;
  authGroup: string;
  role: string;
  status: string;
};

export function normalizeUserSession(user: unknown): UserSession | null {
  if (!user || typeof user !== 'object') return null;
  const row = user as Record<string, unknown>;
  const id = Number(row.id);
  const userAccount = String(row.userAccount ?? '').trim();
  if (!Number.isInteger(id) || id <= 0 || !userAccount) return null;

  return {
    id,
    userAccount,
    userNickName: String(row.userNickName ?? '').trim() || userAccount,
    userSort: String(row.userSort ?? ''),
    userDetail: String(row.userDetail ?? ''),
    grade: String(row.grade ?? ''),
    authInstitution: String(row.authInstitution ?? ''),
    authChurch: String(row.authChurch ?? ''),
    authDepartment: String(row.authDepartment ?? ''),
    authGroup: String(row.authGroup ?? ''),
    role: String(row.role ?? ''),
    status: String(row.status ?? ''),
  };
}

export function getUserSession(consoleKey: ConsoleKey): UserSession | null {
  try {
    const raw = sessionStorage.getItem(storageKey(consoleKey));
    if (!raw) return null;
    return normalizeUserSession(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveUserSession(consoleKey: ConsoleKey, user: unknown) {
  const normalized = normalizeUserSession(user);
  if (!normalized) return;
  sessionStorage.setItem(storageKey(consoleKey), JSON.stringify(normalized));
}

export function clearUserSession(consoleKey: ConsoleKey) {
  sessionStorage.removeItem(storageKey(consoleKey));
}
